import axios from "axios";
import {
  createApiClient,
  getApiBaseUrl,
  getAuthHeader,
  attachRetryInterceptor,
} from "./httpClient";

const API_BASE_URL = `${getApiBaseUrl()}/smartbills`;

// Log API configuration in development and production
console.log("📡 Smart Bill API Configuration:", {
  API_BASE_URL,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL || "not set (using proxy)",
  hostname: typeof window !== "undefined" ? window.location?.hostname : "N/A",
  Proxy:
    process.env.NODE_ENV === "development"
      ? "http://localhost:3000 (configured in package.json)"
      : "Not applicable",
  Note:
    process.env.NODE_ENV === "development"
      ? "Requests will be proxied from frontend (port 3002) to backend (port 3000)"
      : "Using inferred or config API URL",
});

const apiClient = createApiClient(API_BASE_URL);
apiClient.defaults.timeout = 10000;
apiClient.interceptors.request.use((config) => {
  config.headers["Content-Type"] =
    config.headers["Content-Type"] || "application/json";
  return config;
});

const handleResponse = (response) => response.data;

const handleError = (error) => {
  console.error("API Error:", {
    message: error.message,
    url: error.config?.url,
    baseURL: error.config?.baseURL,
    method: error.config?.method,
    status: error.response?.status,
    data: error.response?.data,
  });

  if (error.response?.data) {
    throw error.response.data;
  }

  // Network error or timeout
  if (
    error.message.includes("Network Error") ||
    error.code === "ECONNABORTED"
  ) {
    const networkError = new Error(
      "Network error: Could not connect to server. Please ensure the backend is running on http://localhost:3000",
    );
    networkError.success = false;
    networkError.error = error.message;
    throw networkError;
  }

  const genericError = new Error(error.message || "An error occurred");
  genericError.success = false;
  genericError.error = error.message;
  throw genericError;
};

export const customerApi = {
  getAll: async (search = "") =>
    apiClient
      .get("/customers", { params: { search } })
      .then(handleResponse)
      .catch(handleError),

  getByPhone: async (phone) =>
    apiClient
      .get(`/customers/${phone}`)
      .then(handleResponse)
      .catch(handleError),

  save: async (customerData) =>
    apiClient
      .post("/customers", customerData)
      .then(handleResponse)
      .catch(handleError),
};

export const servicesApi = {
  getAll: async (search = "", category = "", includeInactive = false, extra = {}) =>
    apiClient
      .get("/services", {
        params: { search, category, includeInactive, ...extra },
      })
      .then(handleResponse)
      .catch(handleError),

  getInventory: async (params = {}) =>
    apiClient
      .get("/services/inventory", { params })
      .then(handleResponse)
      .catch(handleError),

  adjustStock: async (id, body) =>
    apiClient
      .post(`/services/${id}/stock`, body)
      .then(handleResponse)
      .catch(handleError),

  getStockMovements: async (id, params = {}) =>
    apiClient
      .get(`/services/${id}/stock-movements`, { params })
      .then(handleResponse)
      .catch(handleError),

  create: async (serviceData) =>
    apiClient
      .post("/services", serviceData)
      .then(handleResponse)
      .catch(handleError),

  update: async (id, serviceData) =>
    apiClient
      .put(`/services/${id}`, serviceData)
      .then(handleResponse)
      .catch(handleError),

  delete: async (id, hardDelete = false) =>
    apiClient
      .delete(`/services/${id}`, { params: { hardDelete } })
      .then(handleResponse)
      .catch(handleError),

  deleteBulk: async (ids, hardDelete = false) =>
    apiClient
      .delete("/services/bulk", { data: { ids, hardDelete } })
      .then(handleResponse)
      .catch(handleError),

  deleteAll: async (hardDelete = false) =>
    apiClient
      .delete("/services/all", { params: { hardDelete } })
      .then(handleResponse)
      .catch(handleError),
};

/** Tally-style main categories — same pool as Mitra Operate */
export const serviceMainCategoriesApi = {
  getAll: async (params = {}) =>
    apiClient
      .get("/service-main-categories", { params })
      .then(handleResponse)
      .catch(handleError),

  create: async (body) =>
    apiClient
      .post("/service-main-categories", body)
      .then(handleResponse)
      .catch(handleError),

  update: async (id, body) =>
    apiClient
      .put(`/service-main-categories/${id}`, body)
      .then(handleResponse)
      .catch(handleError),

  deactivate: async (id) =>
    apiClient
      .delete(`/service-main-categories/${id}`)
      .then(handleResponse)
      .catch(handleError),

  ensureMiscellaneous: async () =>
    apiClient
      .post("/service-main-categories/ensure-miscellaneous")
      .then(handleResponse)
      .catch(handleError),
};

/** Default product/service lines from Citizen taxonomy → Smart Bill `services` */
export const catalogTemplateApi = {
  preview: async (params) =>
    apiClient
      .get("/services/catalog-template-preview", { params })
      .then(handleResponse)
      .catch(handleError),
  resolveMyBinding: async (params) =>
    apiClient
      .get("/services/my-catalog-template-binding", { params })
      .then(handleResponse)
      .catch(handleError),
  apply: async (body) =>
    apiClient
      .post("/services/apply-catalog-template", body)
      .then(handleResponse)
      .catch(handleError),
};

// Professions API - using main API base URL
const getProfessionsApiBaseUrl = () => {
  return getApiBaseUrl();
};

const professionsApiClient = axios.create({
  baseURL: getProfessionsApiBaseUrl(),
  timeout: 10000,
});
attachRetryInterceptor(professionsApiClient);

professionsApiClient.interceptors.request.use(
  (config) => {
    const authHeader = getAuthHeader();
    if (authHeader.Authorization) {
      config.headers.Authorization = authHeader.Authorization;
    }
    config.headers["Content-Type"] =
      config.headers["Content-Type"] || "application/json";
    return config;
  },
  (error) => Promise.reject(error),
);

export const professionsApi = {
  getAll: async (search = "", is_active = "all", page = 1, limit = 100) =>
    professionsApiClient
      .get("/api/professions", { params: { search, is_active, page, limit } })
      .then(handleResponse)
      .catch(handleError),

  getActive: async () =>
    professionsApiClient
      .get("/api/professions/active")
      .then(handleResponse)
      .catch(handleError),

  getById: async (id) =>
    professionsApiClient
      .get(`/api/professions/${id}`)
      .then(handleResponse)
      .catch(handleError),

  create: async (professionData) =>
    professionsApiClient
      .post("/api/professions", professionData)
      .then(handleResponse)
      .catch(handleError),

  update: async (id, professionData) =>
    professionsApiClient
      .put(`/api/professions/${id}`, professionData)
      .then(handleResponse)
      .catch(handleError),

  delete: async (id, hardDelete = false) =>
    professionsApiClient
      .delete(`/api/professions/${id}`, { params: { hardDelete } })
      .then(handleResponse)
      .catch(handleError),
};

export const offersApi = {
  getAll: async (excludeCategories = "") =>
    apiClient
      .get("/offers", { params: { excludeCategories } })
      .then(handleResponse)
      .catch(handleError),
};

export const smartBillDraftsApi = {
  getAll: async (params = {}) =>
    apiClient
      .get("/drafts", { params })
      .then(handleResponse)
      .catch(handleError),

  getById: async (id) =>
    apiClient.get(`/drafts/${id}`).then(handleResponse).catch(handleError),

  save: async (draftData) =>
    apiClient
      .post("/drafts", draftData)
      .then(handleResponse)
      .catch(handleError),

  update: async (id, draftData) =>
    apiClient
      .put(`/drafts/${id}`, draftData)
      .then(handleResponse)
      .catch(handleError),

  remove: async (id) =>
    apiClient.delete(`/drafts/${id}`).then(handleResponse).catch(handleError),
};

export const billsApi = {
  getAll: async (params = {}) =>
    apiClient
      .get("/bills", { params, timeout: 30000 })
      .then(handleResponse)
      .catch(handleError),

  create: async (billData) =>
    apiClient.post("/bills", billData).then(handleResponse).catch(handleError),

  update: async (id, billData) =>
    apiClient
      .put(`/bills/${id}`, billData)
      .then(handleResponse)
      .catch(handleError),

  previewCancelRefund: async (id, params = {}) =>
    apiClient
      .get(`/bills/${id}/cancel-refund/preview`, { params })
      .then(handleResponse)
      .catch(handleError),

  cancelWithRefund: async (id, payload) =>
    apiClient
      .post(`/bills/${id}/cancel-refund`, payload)
      .then(handleResponse)
      .catch(handleError),

  remove: async (id) =>
    apiClient.delete(`/bills/${id}`).then(handleResponse).catch(handleError),

  getServiceChargesChallans: async (params = {}) =>
    apiClient
      .get("/bills/service-charges", { params })
      .then(handleResponse)
      .catch(handleError),

  getDailyServiceChargesTotals: async (params = {}) =>
    apiClient
      .get("/bills/service-charges/daily-totals", { params })
      .then(handleResponse)
      .catch(handleError),

  getServiceBreakdown: async (params = {}) =>
    apiClient
      .get("/bills/service-charges/service-breakdown", { params })
      .then(handleResponse)
      .catch(handleError),

  getCategoryBreakdown: async (params = {}) =>
    apiClient
      .get("/bills/service-charges/category-breakdown", { params })
      .then(handleResponse)
      .catch(handleError),
};

export const customerCouponsApi = {
  getByPhone: async (phone, options = {}) => {
    const config = options.skipAuth ? { headers: { Authorization: "" } } : {};
    return apiClient
      .get(`/customers/${phone}/coupons`, config)
      .then(handleResponse)
      .catch(handleError);
  },

  redeem: async (couponId, data) =>
    apiClient
      .post(`/coupons/${couponId}/redeem`, data)
      .then(handleResponse)
      .catch(handleError),

  remove: async (phone, couponId) =>
    apiClient
      .delete(`/customers/${phone}/coupons/${couponId}`)
      .then(handleResponse)
      .catch(handleError),
};

/** Master coupons from Coupons Management + assign to a customer phone. */
export const couponsManagementApi = {
  getAll: async (params = {}) =>
    axios
      .get(`${getApiBaseUrl()}/coupons`, {
        params,
        headers: { ...getAuthHeader() },
        timeout: 10000,
      })
      .then(handleResponse)
      .catch(handleError),

  /** Fast path for Smart Bill picker — scoped templates, no DigiDrop enrich. */
  getTemplatesForBill: async () =>
    apiClient
      .get("/coupon-templates", { timeout: 6000 })
      .then(handleResponse)
      .catch(async (err) => {
        // Fallback: lite list from /api/coupons (skips DigiDrop count)
        console.warn("coupon-templates fast path failed, trying lite coupons:", err?.message || err);
        return axios
          .get(`${getApiBaseUrl()}/coupons`, {
            params: { status: "active", limit: 100, page: 1, lite: 1, for_bill: 1 },
            headers: { ...getAuthHeader() },
            timeout: 6000,
          })
          .then(handleResponse)
          .catch(handleError);
      }),

  assign: async ({ couponId, customerPhone, customerName }) =>
    axios
      .post(
        `${getApiBaseUrl()}/coupons/assign`,
        {
          coupon_id: couponId,
          customer_phone: customerPhone,
          customer_name: customerName || "",
        },
        {
          headers: { ...getAuthHeader() },
          timeout: 8000,
        },
      )
      .then(handleResponse)
      .catch(handleError),
};

export const enquiriesApi = {
  getAll: async (params = {}) => {
    const baseUrl = API_BASE_URL.replace("/smartbills", "/enquiries");
    return axios
      .get(baseUrl, { params, headers: { ...getAuthHeader() } })
      .then(handleResponse)
      .catch(handleError);
  },

  getById: async (id) => {
    const baseUrl = API_BASE_URL.replace("/smartbills", "/enquiries");
    return axios
      .get(`${baseUrl}/${id}`, { headers: { ...getAuthHeader() } })
      .then(handleResponse)
      .catch(handleError);
  },

  create: async (enquiryData) => {
    const baseUrl = API_BASE_URL.replace("/smartbills", "/enquiries");
    return axios
      .post(baseUrl, enquiryData, { headers: { ...getAuthHeader() } })
      .then(handleResponse)
      .catch(handleError);
  },

  update: async (id, enquiryData) => {
    const baseUrl = API_BASE_URL.replace("/smartbills", "/enquiries");
    return axios
      .put(`${baseUrl}/${id}`, enquiryData, { headers: { ...getAuthHeader() } })
      .then(handleResponse)
      .catch(handleError);
  },

  delete: async (id) => {
    const baseUrl = API_BASE_URL.replace("/smartbills", "/enquiries");
    return axios
      .delete(`${baseUrl}/${id}`, { headers: { ...getAuthHeader() } })
      .then(handleResponse)
      .catch(handleError);
  },

  convert: async (id) => {
    const baseUrl = API_BASE_URL.replace("/smartbills", "/enquiries");
    return axios
      .post(`${baseUrl}/${id}/convert`, {}, { headers: { ...getAuthHeader() } })
      .then(handleResponse)
      .catch(handleError);
  },
};

// Job Alerts API - for bill services dropdown
const getJobAlertsApiBaseUrl = () => {
  return getApiBaseUrl();
};

const jobAlertsApiClient = axios.create({
  baseURL: getJobAlertsApiBaseUrl(),
  timeout: 10000,
});
attachRetryInterceptor(jobAlertsApiClient);

jobAlertsApiClient.interceptors.request.use(
  (config) => {
    const authHeader = getAuthHeader();
    if (authHeader.Authorization) {
      config.headers.Authorization = authHeader.Authorization;
    }
    config.headers["Content-Type"] =
      config.headers["Content-Type"] || "application/json";
    return config;
  },
  (error) => Promise.reject(error),
);

export const jobAlertsApi = {
  // Get active job alerts only (for bill services dropdown)
  getActive: async (search = "", limit = 100) =>
    jobAlertsApiClient
      .get("/api/jobalerts/public", {
        params: {
          isActive: "true", // Only fetch active job alerts
          search,
          limit,
        },
      })
      .then(handleResponse)
      .catch(handleError),
};
